// src/services/keepAlive.js
const API_URL = "https://final-project-5ar7.onrender.com";
console.log('🌐 KEEPALIVE API_URL:', API_URL);

class KeepAliveService {
  constructor() {
    this.intervalId = null;
    this.isActive = false;
    this.lastPingTime = null;
    this.failureCount = 0;
    this.userType = null;
    this.userId = null;
  }

  // ✅ Start keep-alive for logged-in user
  start(userInfo = {}) {
    if (this.isActive) {
      console.log('⚠️ KeepAlive: Already running, skipping start');
      return;
    }
    
    this.isActive = true;
    this.userType = userInfo.isAdmin ? 'admin' : 'user';
    this.userId = userInfo.id;
    
    console.log(`🔄 KeepAlive: Starting for ${this.userType} (${userInfo.email})`);
    
    // Different ping intervals based on user type
    const pingInterval = this.userType === 'admin' ? 8 * 60 * 1000 : 12 * 60 * 1000;
    // Admin: 8 minutes, User: 12 minutes
    
    this.intervalId = setInterval(() => {
      this.pingBackend();
    }, pingInterval);
    
    // Initial ping after 5 seconds
    setTimeout(() => this.pingBackend(), 5000);
  }

  // ✅ Stop keep-alive
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    const userInfo = this.userType ? `${this.userType} (${this.userId})` : 'Unknown';
    console.log(`⏹️ KeepAlive: Stopped for ${userInfo}`);
    
    this.isActive = false;
    this.failureCount = 0;
    this.userType = null;
    this.userId = null;
    this.lastPingTime = null;
  }

  // ✅ Ping backend to keep it awake
  async pingBackend() {
    try {
      console.log(`📡 KeepAlive: Pinging backend for ${this.userType}...`);
      
      const response = await fetch(`${API_URL}/api/health`, {  // ✅ FIXED: API_URL instead of API_BASE_URL
        method: 'GET',
        headers: {
          'X-User-Type': this.userType || 'unknown',
          'X-User-ID': this.userId || 'unknown'
        },
        signal: AbortSignal.timeout(30000) // 30 second timeout
      });

      if (response.ok) {
        this.lastPingTime = new Date();
        this.failureCount = 0;
        console.log(`✅ KeepAlive: Backend alive for ${this.userType}`);
        
        // Optional: Log backend response
        const data = await response.json();
        if (data.message) {
          console.log(`📊 Backend Status: ${data.message}`);
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
      
    } catch (error) {
      this.failureCount++;
      console.warn(`⚠️ KeepAlive: Ping failed for ${this.userType} (${this.failureCount}/3):`, error.message);
      
      // If 3 consecutive failures, try to wake up backend
      if (this.failureCount >= 3) {
        await this.wakeUpBackend();
      }
    }
  }

  // ✅ Wake up sleeping backend
  async wakeUpBackend() {
    console.log(`🚨 KeepAlive: Backend appears sleeping, attempting wake-up for ${this.userType}...`);
    
    try {
      // Multiple wake-up requests
      const wakeUpEndpoints = [
        `${API_URL}/api/health`,          // ✅ FIXED: API_URL
        `${API_URL}/api/auth/ping`,       // ✅ FIXED: API_URL
        `${API_URL}/`,                    // ✅ FIXED: API_URL
        // User-specific wake-up
        this.userType === 'admin' 
          ? `${API_URL}/api/admin/wake-up`    // ✅ FIXED: API_URL
          : `${API_URL}/api/user/wake-up`     // ✅ FIXED: API_URL
      ];
      
      const wakeUpPromises = wakeUpEndpoints.map(url => 
        fetch(url, { 
          method: 'GET',
          signal: AbortSignal.timeout(15000) // 15 second timeout for wake-up
        }).catch(error => console.log(`Wake-up request to ${url} failed:`, error.message))
      );
      
      await Promise.allSettled(wakeUpPromises);
      
      // Wait for backend to fully wake up
      console.log(`⏰ KeepAlive: Giving backend 10 seconds to wake up for ${this.userType}...`);
      setTimeout(() => {
        this.failureCount = Math.max(0, this.failureCount - 2); // Reduce failure count
        console.log(`🔄 KeepAlive: Backend should be awake now for ${this.userType}`);
      }, 10000);
      
    } catch (error) {
      console.error(`❌ KeepAlive: Wake-up failed for ${this.userType}:`, error);
    }
  }

  // ✅ Status checks
  isBackendSleeping() {
    return this.failureCount >= 2;
  }

  getStatus() {
    return {
      isActive: this.isActive,
      userType: this.userType,
      userId: this.userId,
      lastPingTime: this.lastPingTime,
      failureCount: this.failureCount,
      backendSleeping: this.isBackendSleeping(),
      lastPingTimeFormatted: this.lastPingTime ? this.lastPingTime.toLocaleTimeString() : 'Never'
    };
  }

  // ✅ Force immediate ping (for testing)
  async forcePing() {
    console.log('🔧 KeepAlive: Force ping requested');
    await this.pingBackend();
  }
}

// ✅ Export singleton instance
export const keepAliveService = new KeepAliveService();
export default keepAliveService;
